import { Avatar, Card, Collection, CollectionItem, CollectionLink, PageHeader } from '@ksyos/design-system';
import { Link as ReactRouterLink, useParams, useSearchParams } from '@ksyos/router';
import { useSelector } from 'react-redux';
import type { IReduxState } from '@frontend/configureStore';
import { ManagerAuthorization } from '@common/enums/index';
import { getDisplayInitials } from '@common/utils/getDisplayInitials';
import { getDisplayName } from '@common/utils/getDisplayName';
import { getSelectedLanguage, translateKey } from '@frontend/translate';
import { hasOneAuthorization } from '@frontend/utils';
import { useFrontendSettings } from '@frontend/utils/hooks/useFrontendSettings';
import { EmploymentTaxesDetailsCard } from '../employmentTaxesDetails/EmploymentTaxesDetailsCard';
import { ContactDetails } from './ContactDetails';
import { OrganizationPayoutManagerSettings } from './OrganizationPayoutManagerSettings';
import { OrganizationStamp } from './OrganizationStamp';
import { PaymentDetails } from './PaymentDetails';
import { usePersonalSettings } from './PersonalSettingsContext';
import { PersonalSettingsGeneralSettings } from './personalSettingsGeneralSettings/PersonalSettingsGeneralSettings';
import { ZorgMailSettings } from './ZorgMailSettings';

export function PersonalSettingsOverview() {
    const translate = getSelectedLanguage();
    const [searchParams] = useSearchParams();
    const { frontendSettings } = useFrontendSettings();
    const { personalSettingsData, isReadOnly } = usePersonalSettings();
    const { employmentId } = useParams() as { employmentId: string };

    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);
    const isCurrentUser =
        useSelector((state: IReduxState) => state.authentication.id) === personalSettingsData?.practitioner.id;

    const editForm = searchParams.get('editForm');
    const focusField = searchParams.get('focusField');

    const hasActiveServiceConsumer = (personalSettingsData?.serviceConsumers || []).length > 0;

    if (!personalSettingsData) {
        return null;
    }

    return (
        <>
            <PageHeader title={translate.common.personalSettings} />
            <PersonalSettingsGeneralSettings
                employmentId={employmentId}
                isCurrentUser={isCurrentUser}
                isReadOnly={isReadOnly}
                personalSettingData={personalSettingsData}
            />
            <ContactDetails
                employmentId={employmentId}
                enableEditByDefault={editForm === 'contactDetails'}
                focusFieldByDefault={focusField ?? undefined}
                isReadOnly={isReadOnly}
            />
            {(hasActiveServiceConsumer ||
                hasOneAuthorization([
                    ManagerAuthorization.employmentSettingsRead,
                    ManagerAuthorization.employmentSettingsEdit,
                ])) && (
                <ZorgMailSettings
                    employmentId={employmentId}
                    hasActiveServiceConsumer={hasActiveServiceConsumer}
                    isReadOnly={isReadOnly}
                />
            )}

            <PaymentDetails
                employmentId={employmentId}
                isCurrentUser={isCurrentUser}
                isReadOnly={
                    (isReadOnly && !hasOneAuthorization(ManagerAuthorization.employmentRelationNumberEdit)) ||
                    isCurrentUser
                }
            />

            <EmploymentTaxesDetailsCard employmentId={employmentId} />

            <OrganizationStamp
                employmentId={employmentId}
                isReadOnly={isReadOnly}
                organizationId={personalSettingsData?.organization.id}
            />

            {frontendSettings.organizationPayoutManagerEnabled && (
                <OrganizationPayoutManagerSettings
                    employmentId={employmentId}
                    isReadOnly={isReadOnly}
                />
            )}

            {isManager && (
                <Card>
                    <Collection type="nav">
                        {hasOneAuthorization(ManagerAuthorization.practitionerRead) ? (
                            <CollectionLink
                                as={ReactRouterLink}
                                mediaItem={
                                    <Avatar
                                        imageUrl={personalSettingsData.practitioner.profilePictureUrl}
                                        initials={getDisplayInitials(personalSettingsData.practitioner)}
                                        size={56}
                                    />
                                }
                                subtitle={
                                    personalSettingsData.practitioner.uid
                                        ? personalSettingsData.practitioner.uid.toString()
                                        : translateKey('profile.viewProfile')
                                }
                                title={
                                    personalSettingsData.practitioner &&
                                    getDisplayName(personalSettingsData.practitioner, false, 'nameGivenNameFamily')
                                }
                                to={`/practitioners/${personalSettingsData.practitioner.id}/profile`}
                            />
                        ) : (
                            <CollectionItem
                                mediaItem={
                                    <Avatar
                                        imageUrl={personalSettingsData.practitioner.profilePictureUrl}
                                        initials={getDisplayInitials(personalSettingsData.practitioner)}
                                        size={56}
                                    />
                                }
                                subtitle={
                                    personalSettingsData.practitioner.uid
                                        ? personalSettingsData.practitioner.uid.toString()
                                        : translateKey('profile.viewProfile')
                                }
                                title={
                                    personalSettingsData.practitioner &&
                                    getDisplayName(personalSettingsData.practitioner, false, 'nameGivenNameFamily')
                                }
                            />
                        )}
                    </Collection>
                </Card>
            )}
        </>
    );
}
